// react core
import { useContext, useState } from "react";

// material design
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";

// fonts and icons
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImageLandscape as iconImageFallback } from "@fortawesome/pro-thin-svg-icons";

// entzy config and context
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { ActionButton } from "components/utils/common/CommonButtons";
import { ActionLoader } from "components/utils/common/CommonLoaders";
import { ActiveTag } from "components/utils/common/CommonTags";
import { timeout } from "models/Tools";

// entzy widgets and services
import { serviceObjectImageState } from "services/storage/object";

// entzy hooks
import { useMediaImage } from "hooks/media/mediaImage";

function ActionLaunchpadFrame(props) {
  const user = props.user;
  const handleEdit = props.handleEdit;

  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);

  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState("");

  // capture current frame details;
  const frameId = props.frame.id;
  const frameCustom = props.frame.tag === "frameCustom";
  const frameName = frameCustom
    ? props.frame.name
    : frameId.replace("frame", "Frame ");
  const formFrameId =
    "event-" + eventContext.state.event.data.EventId + "-image-" + frameId;
  const frameData = eventContext.state.event.data.ImageFrames
    ? eventContext.state.event.data.ImageFrames.filter(
        (obj) => obj.id === frameId
      )
    : [];
  const framePath = frameData.length
    ? frameData[0].path
    : configEntzy.EMPTY_STRING_SHORTSET;
  const frameMultiple = props.frame.multiple || false;

  // populate image frames onto latest hangers if exist
  const imageFrames = eventContext.hangImageFrames();

  const handleImageFrame = async (data) => {
    if (eventContext.state.event.manager) {
      const uploadedValue =
        data === configEntzy.EMPTY_STRING_SHORTSET
          ? { key: configEntzy.EMPTY_STRING_SHORTSET }
          : data.filter((obj) => obj.id === formFrameId)[0].value;
      const uploadedImages = Array.isArray(uploadedValue)
        ? uploadedValue
        : [uploadedValue];

      // validate all images uploaded
      let allValid = true;
      for (let i = 0; i < uploadedImages.length; i++) {
        const img = uploadedImages[i];
        if (!img || !img.key || !img.key.length) {
          allValid = false;
          break;
        }
      }
      if (!allValid) {
        return {
          alert: true,
          message: "All images must be uploaded before proceeding",
        };
      }

      // let count = 0,
      //   ready = false;
      // setLoading(true);
      // if (uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET) {
      //   // this is a remove image request
      //   uploadedImage.preload = null;
      //   ready = true;
      // } else {
      //   // for new image first check all resized images are available
      //   do {
      //     await timeout(count * 1000);
      //     ready = await serviceObjectImageState(uploadedImage.key);
      //     count++;
      //   } while (count < 10 && !ready);
      // }
      // // proceed when ready
      // if (ready) {
      //   imageFrames.forEach((frame, index) => {
      //     if (frame.id === frameId) {
      //       frame.path = uploadedImage.key;
      //       frame.active =
      //         uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET
      //           ? false
      //           : true;
      //     }
      //   });
      //   const eventUpdate = await eventContext.prepareUpdateEvent({
      //     key: "ImageFrames",
      //     value: imageFrames,
      //     preload: uploadedImage.preload,
      //   });
      //   setLoading(false);
      //   setSelection("");
      //   if (eventUpdate.alert) {
      //     return {
      //       alert: true,
      //       message: eventUpdate.message,
      //     };
      //   } else {
      //     eventContext.updateEvent(eventUpdate);
      //     return { alert: false };
      //   }
      // } else {
      //   setLoading(false);
      //   setSelection("");
      //   return {
      //     alert: true,
      //     message:
      //       "Image could not be processed. Give it another go or if this problem persists try another image size or format.",
      //   };
      // }

      // refactor above to handle multiple images and process all in parallel
      setLoading(true);
      const processImagePromises = uploadedImages.map(
        async (uploadedImage, index) => {
          let imageId = frameId === "frame" ? `frame${index + 1}` : frameId;
          let count = 0,
            ready = false;
          if (uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET) {
            // this is a remove image request
            uploadedImage.preload = null;
            ready = true;
          } else {
            // for new image first check all resized images are available
            do {
              await timeout(count * 1000);
              ready = await serviceObjectImageState(uploadedImage.key);
              count++;
            } while (count < 10 && !ready);
          }
          return { id: imageId, uploadedImage, ready };
        }
      );

      const processedImages = await Promise.all(processImagePromises);
      let allProcessedValid = true;
      processedImages.forEach(({ uploadedImage, ready }) => {
        if (!ready) {
          allProcessedValid = false;
        }
      });

      if (allProcessedValid) {
        processedImages.forEach(({ id, uploadedImage }) => {
          imageFrames.forEach((frame) => {
            if (frame.id === id) {
              frame.path = uploadedImage.key;
              frame.active =
                uploadedImage.key === configEntzy.EMPTY_STRING_SHORTSET
                  ? false
                  : true;
            }
          });
        });
        const eventUpdate = await eventContext.prepareUpdateEvent({
          key: "ImageFrames",
          value: imageFrames,
          // preload: uploadedImage.preload,
        });
        setLoading(false);
        setSelection("");
        if (eventUpdate.alert) {
          return {
            alert: true,
            message: eventUpdate.message,
          };
        } else {
          eventContext.updateEvent(eventUpdate);
          handleEdit(null, frameId);
          return { alert: false };
        }
      } else {
        setLoading(false);
        setSelection("");
        return {
          alert: true,
          message:
            "One or more images could not be processed. Give it another go or if this problem persists try another image size or format.",
        };
      }
    } else {
      return {
        alert: true,
        message: "Eventuator runner permissions required for this update",
      };
    }
  };

  // load page images
  const mediaImage = useMediaImage(
    eventContext.state.event.hydrated
      ? {
          user: user,
          context: mainContext,
          image: {
            id:
              "event:frames:" +
              frameId +
              ":" +
              eventContext.state.event.data.EventId,
            key: framePath,
          },
        }
      : null
  );

  return (
    <Box className="box-default">
      {loading || !mediaImage ? (
        <Box
          className="box-default"
          sx={{
            pt: configEntzy.APP_SPACING_XL,
            pb: configEntzy.APP_SPACING_XL,
          }}
        >
          <ActionLoader size="md" color="success" />
        </Box>
      ) : (
        <Box
          className="box-default"
          sx={{ mb: configEntzy.APP_SPACING_MD }}
          hidden={selection && selection !== "logo"}
        >
          {framePath !== configEntzy.EMPTY_STRING_SHORTSET ? (
            <Box
              className="box-default bg-black-t50 shadow-dark"
              sx={{
                p: configEntzy.APP_SPACING_MD,
                borderRadius: configEntzy.BORDER_SIZE_XL,
              }}
            >
              <Box className="box-default relative">
                <Box
                  className="box-default"
                  sx={{
                    "&::before": {
                      content: `''`,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundImage:
                        mediaImage.hydrated && mediaImage.images.lg
                          ? "url(" + mediaImage.images.lg.data + ")"
                          : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "repeat-y",
                      filter:
                        mediaImage.hydrated && mediaImage.images.lg
                          ? "blur(1px)"
                          : "none",
                      opacity: 1,
                      zIndex: -1,
                    },
                  }}
                >
                  <Box
                    className="box-default"
                    sx={{
                      pt: configEntzy.APP_SPACING_XL,
                      pb: configEntzy.APP_SPACING_XL,
                    }}
                  >
                    <Box className="box-default">
                      <ActiveTag prefix={frameName} value={true} />
                    </Box>
                    <Box
                      className="box-default"
                      sx={{
                        mt: configEntzy.APP_SPACING_MD,
                      }}
                    >
                      <ActionButton
                        size="small"
                        text="Remove"
                        color="danger"
                        onClick={() =>
                          handleImageFrame(configEntzy.EMPTY_STRING_SHORTSET)
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box className="box-default">
              <Collapse
                in={true}
                timeout={{
                  enter: configEntzy.TRANSITIONS.default,
                  exit: configEntzy.TRANSITIONS.default,
                }}
              >
                <Box
                  className="box-default"
                  sx={{ mt: configEntzy.APP_SPACING_MD }}
                >
                  <InputForm
                    user={user}
                    navigate={props.navigate}
                    fields={[
                      {
                        id: formFrameId,
                        type: "image-picker",
                        level: "public",
                        value: "",
                        image:
                          framePath !== configEntzy.EMPTY_STRING_SHORTSET
                            ? framePath
                            : null,
                        required: true,
                        disablePadding: true,
                        multiple: frameMultiple,
                      },
                    ]}
                    submitText="All Good? Confirm Update"
                    submitType="flow"
                    openImagePicker={true}
                    fallbackImage={iconImageFallback}
                    callback={handleImageFrame}
                    confirmation={"Image " + frameId + " updated successfully"}
                    // cancel={handleShowCreate}
                    // cancelText="Cancel"
                    // footNotes={["Background logo for your eventuator"]}
                  />
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ActionLaunchpadFrame;
